/**
 * Keyboard movement in the grid (1.N-2 deliverable 7): the WAI-ARIA grid pattern. Row 0 is the
 * header row; rows 1 to `rows` are the loaded body rows. Pure.
 */
export interface GridPos {
  readonly row: number;
  readonly col: number;
}

export interface GridBounds {
  /** Loaded body rows. */
  readonly rows: number;
  /** Columns shown. */
  readonly cols: number;
  /** Rows the server holds in all. */
  readonly total: number;
  /** Whether another page can be fetched. */
  readonly hasMore: boolean;
}

export type GridKey =
  | 'ArrowUp'
  | 'ArrowDown'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'Home'
  | 'End'
  | 'PageUp'
  | 'PageDown';

export const PAGE_ROWS = 10;

export type GridMove =
  | { readonly kind: 'move'; readonly pos: GridPos }
  | { readonly kind: 'fetchMore'; readonly pos: GridPos }
  | { readonly kind: 'stay' };

const stay: GridMove = { kind: 'stay' };

export function isGridKey(key: string): key is GridKey {
  return (
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'Home' ||
    key === 'End' ||
    key === 'PageUp' ||
    key === 'PageDown'
  );
}

/**
 * Where `key` takes `pos`. Moving below the loaded rows answers `fetchMore` with the cell to land
 * on once the page has loaded (clamped to the table's total); at the end of the table it stays.
 */
export function nextCell(pos: GridPos, key: GridKey, bounds: GridBounds, mod = false): GridMove {
  const lastCol = Math.max(0, bounds.cols - 1);
  const lastRow = bounds.rows;
  const move = (row: number, col: number): GridMove => {
    const next = { row, col };
    return next.row === pos.row && next.col === pos.col ? stay : { kind: 'move', pos: next };
  };
  const down = (target: number): GridMove => {
    if (target <= lastRow) return move(target, pos.col);
    if (bounds.hasMore && lastRow < bounds.total) {
      return { kind: 'fetchMore', pos: { row: Math.min(target, bounds.total), col: pos.col } };
    }
    return move(lastRow, pos.col);
  };
  switch (key) {
    case 'ArrowLeft':
      return move(pos.row, Math.max(0, pos.col - 1));
    case 'ArrowRight':
      return move(pos.row, Math.min(lastCol, pos.col + 1));
    case 'ArrowUp':
      return move(Math.max(0, pos.row - 1), pos.col);
    case 'ArrowDown':
      return down(pos.row + 1);
    case 'Home':
      return mod ? move(Math.min(1, lastRow), 0) : move(pos.row, 0);
    case 'End':
      return mod ? move(lastRow, lastCol) : move(pos.row, lastCol);
    case 'PageUp':
      return pos.row === 0 ? stay : move(Math.max(1, pos.row - PAGE_ROWS), pos.col);
    case 'PageDown':
      return down(pos.row + PAGE_ROWS);
  }
}
